'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useState } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { FundingAddress } from '../shared/funding-address';
import { useFundingStage } from '../shared/funding-chrome';
import { FundingQr } from '../shared/funding-qr';
import { useManualTransferWallet } from './use-manual-transfer-wallet';
import './funding-manual-transfer.css';
export function FundingManualTransfer(t0) {
  const $ = _c(47);
  const {
    onBack,
    onCryptoDeposit
  } = t0;
  const t = useTranslations("onramp");
  const wallet = useManualTransferWallet();
  const [copyFailureAddress, setCopyFailureAddress] = useState(null);
  let t1;
  if ($[0] !== t) {
    t1 = t("manual_transfer");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useFundingStage(t1, false, onBack);
  let t2;
  let t3;
  if ($[2] !== wallet.network) {
    t2 = () => {
      onEvent("onramp_manual_transfer_opened", {
        platform: "web",
        surface: "manual_transfer",
        network: wallet.network
      });
    };
    t3 = [wallet.network];
    $[2] = wallet.network;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[5] !== wallet.errorCode || $[6] !== wallet.network) {
    t4 = () => {
      if (wallet.errorCode) {
        logger.warn("onramp.manual_transfer_wallet_failed", {
          platform: "web",
          surface: "manual_transfer",
          network: wallet.network,
          code: wallet.errorCode
        });
      }
    };
    t5 = [wallet.errorCode, wallet.network];
    $[5] = wallet.errorCode;
    $[6] = wallet.network;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== t || $[10] !== wallet.address || $[11] !== wallet.network) {
    t6 = async function copy() {
      if (!wallet.address) {
        return;
      }
      setCopyFailureAddress(null);
      try {
        await navigator.clipboard.writeText(wallet.address);
      } catch {
        setCopyFailureAddress(wallet.address);
        logger.warn("onramp.manual_transfer_copy_failed", {
          platform: "web",
          surface: "manual_transfer",
          network: wallet.network,
          code: "clipboard_write_failed"
        });
        return;
      }
      showToast({
        id: TOAST_IDS.WALLET_ADDRESS_COPIED,
        message: t("manual_transfer_copied"),
        variant: "success"
      });
      onEvent("onramp_manual_transfer_address_copied", {
        platform: "web",
        surface: "manual_transfer",
        network: wallet.network
      });
    };
    $[9] = t;
    $[10] = wallet.address;
    $[11] = wallet.network;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const copy = t6;
  if (wallet.status === "loading") {
    let t7;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Skeleton className="my-2 h-12 w-full rounded-control" />;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    let t8;
    if ($[14] !== t) {
      t8 = t("manual_transfer_loading");
      $[14] = t;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    let t9;
    if ($[16] !== t8) {
      t9 = <output aria-busy="true" className="flex flex-col gap-4">{t7}<p className="text-body text-foreground-secondary">{t8}</p></output>;
      $[16] = t8;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    return t9;
  }
  if (!wallet.address || !wallet.qrValue) {
    let t7;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <ManualArtwork src="/assets/onramp/status-offline.png" size="size-26" />;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    let t8;
    if ($[19] !== t) {
      t8 = t("manual_transfer_unavailable");
      $[19] = t;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    let t9;
    if ($[21] !== t8) {
      t9 = <h3 className="text-lg font-medium">{t8}</h3>;
      $[21] = t8;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    let t10;
    if ($[23] !== t) {
      t10 = t("manual_transfer_retry");
      $[23] = t;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    let t11;
    if ($[25] !== t10) {
      t11 = <p className="text-body text-foreground-secondary">{t10}</p>;
      $[25] = t10;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    let t12;
    if ($[27] !== wallet) {
      t12 = () => {
        wallet.retry().catch(() => logger.warn("onramp.manual_transfer_wallet_failed", {
          platform: "web",
          surface: "manual_transfer",
          network: wallet.network,
          code: "wallet_retry_failed"
        }));
      };
      $[27] = wallet;
      $[28] = t12;
    } else {
      t12 = $[28];
    }
    let t13;
    if ($[29] !== t) {
      t13 = t("retry");
      $[29] = t;
      $[30] = t13;
    } else {
      t13 = $[30];
    }
    let t14;
    if ($[31] !== t12 || $[32] !== t13) {
      t14 = <AnimatedButton className="h-11 w-full" onClick={t12}>{t13}</AnimatedButton>;
      $[31] = t12;
      $[32] = t13;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    let t15;
    if ($[34] !== t11 || $[35] !== t14 || $[36] !== t9) {
      t15 = <div className="flex flex-col gap-4">{t7}{t9}{t11}{t14}</div>;
      $[34] = t11;
      $[35] = t14;
      $[36] = t9;
      $[37] = t15;
    } else {
      t15 = $[37];
    }
    return t15;
  }
  const t7 = copyFailureAddress === wallet.address;
  let t8;
  if ($[38] !== onCryptoDeposit || $[39] !== wallet.network) {
    t8 = onCryptoDeposit ? () => {
      onEvent("onramp_manual_transfer_alternate_crypto_selected", {
        platform: "web",
        surface: "manual_transfer",
        network: wallet.network
      });
      onCryptoDeposit();
    } : undefined;
    $[38] = onCryptoDeposit;
    $[39] = wallet.network;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  let t9;
  if ($[41] !== copy || $[42] !== t7 || $[43] !== t8 || $[44] !== wallet.address || $[45] !== wallet.qrValue) {
    t9 = <FundingManualTransferView address={wallet.address} qrValue={wallet.qrValue} onCopy={copy} copyFailed={t7} onAlternate={t8} />;
    $[41] = copy;
    $[42] = t7;
    $[43] = t8;
    $[44] = wallet.address;
    $[45] = wallet.qrValue;
    $[46] = t9;
  } else {
    t9 = $[46];
  }
  return t9;
}
export function FundingManualTransferView(t0) {
  const $ = _c(39);
  const {
    address,
    qrValue,
    onCopy,
    copyFailed: t1,
    onAlternate
  } = t0;
  const copyFailed = t1 === undefined ? false : t1;
  const t = useTranslations("onramp");
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ManualArtwork src="/assets/onramp/usdc.svg" size="size-7" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== t) {
    t3 = t("manual_transfer_asset");
    $[1] = t;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t3) {
    t4 = <div className="flex items-center justify-center gap-2">{t2}<strong className="text-body">{t3}</strong></div>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t) {
    t5 = t("manual_transfer_asset");
    $[5] = t;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== qrValue) {
    t6 = <FundingQr value={qrValue} />;
    $[7] = qrValue;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <div className="funding-manual-qr relative isolate flex min-h-68 items-center justify-center overflow-hidden rounded-control bg-background-tertiary py-6"><div role="img" className="relative overflow-hidden rounded-control bg-white shadow-lg" aria-label={t5}>{t6}</div></div>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t) {
    t8 = t("manual_transfer_address");
    $[12] = t;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <p className="text-caption text-foreground-secondary">{t8}</p>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== address || $[17] !== onCopy) {
    t10 = <FundingAddress address={address} onCopy={onCopy} />;
    $[16] = address;
    $[17] = onCopy;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== copyFailed || $[20] !== t) {
    t11 = copyFailed ? <p role="alert" className="text-caption text-feedback-error-content">{t("manual_transfer_copy_failed")}</p> : null;
    $[19] = copyFailed;
    $[20] = t;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t9) {
    t12 = <div className="flex flex-col gap-2.5 text-center">{t9}{t10}{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t) {
    t13 = t("manual_transfer_warning");
    $[26] = t;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t13) {
    t14 = <p className="text-center text-xs leading-4.5 text-foreground-secondary">{t13}</p>;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== onAlternate || $[31] !== t) {
    t15 = onAlternate ? <AnimatedButton variant="tertiary" className="w-full underline underline-offset-3" onClick={onAlternate}>{t("manual_transfer_alternate")}</AnimatedButton> : null;
    $[30] = onAlternate;
    $[31] = t;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t12 || $[34] !== t14 || $[35] !== t15 || $[36] !== t4 || $[37] !== t7) {
    t16 = <div className="flex flex-col gap-5">{t4}{t7}{t12}{t14}{t15}</div>;
    $[33] = t12;
    $[34] = t14;
    $[35] = t15;
    $[36] = t4;
    $[37] = t7;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  return t16;
}
function ManualArtwork(t0) {
  const $ = _c(3);
  const {
    src,
    size
  } = t0;
  let t1;
  if ($[0] !== size || $[1] !== src) {
    t1 = <OptimizedImage nsfw={false} variant="thumbnail" preview={false} loadReveal="none" rounded="" imageClassName="object-contain" className="bg-transparent" src={src} size={size} alt="" />;
    $[0] = size;
    $[1] = src;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}